/**
 * Browser-side Shopify client. This is a *thin* wrapper that calls our own
 * Express endpoints (`/api/*`); the server holds the Admin API token and talks
 * to Shopify on our behalf. Doing the cart lookup directly from the browser
 * with a Storefront token is not possible because chekly passes Shopify's
 * Ajax-cart `cart_token`, not a Storefront `cart.id`.
 *
 * In v1 (no Shopify credentials yet), all of these fall back to the prototype's
 * hardcoded data from `config/settings.js` so the UI is fully functional.
 */

import { FALLBACK_CART_ITEMS, FALLBACK_UPSELL_PRODUCTS } from '../config/settings.js';

const API_BASE = '/api';

async function callApi(path, init) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'content-type': 'application/json' },
      ...init,
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  } catch (err) {
    // In dev / when the server isn't reachable, fall through to fallback.
    return null;
  }
}

/**
 * Raised when a REAL customer's cart could not be loaded. Carries whether a
 * retry could plausibly help, so the UI can offer the right action.
 */
export class CartLoadError extends Error {
  constructor(message, { status = 0, permanent = false } = {}) {
    super(message);
    this.name = 'CartLoadError';
    this.status = status;
    this.permanent = permanent;
  }
}

/**
 * Fetch the customer's Shopify cart.
 *
 * When storefront params are present this is a REAL customer arriving from the
 * theme's checkout redirect, and a failure MUST be surfaced. It used to fall
 * through to FALLBACK_CART_ITEMS, which showed the customer a demo product
 * they never added, with cart.id = null so no order could ever be placed. On
 * 2026-09-01 that hid a 5-hour outage from 11 of 12 customers — they simply saw
 * the wrong product and left.
 *
 * With NO params (dev/preview/direct visit) the demo cart is still returned,
 * which is what makes the design previewable without a live Shopify cart.
 */
export async function getCart({ shop, cartToken } = {}) {
  if (shop && cartToken) {
    let res;
    try {
      res = await fetch(`${API_BASE}/cart?shop=${encodeURIComponent(shop)}&token=${encodeURIComponent(cartToken)}`, {
        headers: { 'content-type': 'application/json' },
      });
    } catch (err) {
      // Transport failure (offline, DNS, aborted) — retrying may well work.
      throw new CartLoadError('network', { status: 0, permanent: false });
    }
    if (!res.ok) {
      // 503 = app not installed for this shop, 404 = cart gone/expired.
      // Neither is fixed by pressing retry.
      throw new CartLoadError('http', {
        status: res.status,
        permanent: res.status === 503 || res.status === 404 || res.status === 400,
      });
    }
    const data = await res.json().catch(() => null);
    if (!data?.lines) throw new CartLoadError('malformed', { status: res.status, permanent: false });
    return data;
  }
  // No storefront params at all → dev/preview/direct visit.
  return {
    id: null,
    lines: FALLBACK_CART_ITEMS.map((i) => ({ ...i })),
    subtotal: FALLBACK_CART_ITEMS.reduce((a, i) => a + i.price * i.qty, 0),
    currency: 'грн',
  };
}

export async function getRecommendations({ shop, productId } = {}) {
  if (shop && productId) {
    const data = await callApi(`/recommendations?shop=${encodeURIComponent(shop)}&product=${encodeURIComponent(productId)}`);
    if (data?.products) return data.products;
  }
  return FALLBACK_UPSELL_PRODUCTS.map((p) => ({ ...p }));
}

/**
 * Mutate cart lines on Shopify so totals + discount stay in sync.
 * Each helper returns the updated, shaped cart (same shape as getCart),
 * or null on failure.
 */

async function mutateLines(payload) {
  const res = await fetch(`${API_BASE}/cart/lines`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.code || null;
    // A 409 stock rejection still carries the corrected cart — callers should
    // render it so the UI matches Shopify instead of keeping stale state.
    err.cart = data?.cart || null;
    throw err;
  }
  return data.cart;
}

export async function addLine({ shop, cartId, merchandiseId, quantity = 1 }) {
  return mutateLines({ shop, cartId, action: 'add', lines: [{ merchandiseId, quantity }] });
}

export async function updateLine({ shop, cartId, lineId, quantity }) {
  return mutateLines({ shop, cartId, action: 'update', lines: [{ id: lineId, quantity }] });
}

export async function removeLine({ shop, cartId, lineId }) {
  return mutateLines({ shop, cartId, action: 'remove', lineIds: [lineId] });
}

/** Remove several lines in one round-trip (used to clear sold-out items). */
export async function removeLines({ shop, cartId, lineIds }) {
  return mutateLines({ shop, cartId, action: 'remove', lineIds });
}

/**
 * Apply (or clear if `code` is empty) a discount code against the live cart.
 * Delegates entirely to Shopify via /api/cart/discount.
 *
 * When `phone` is provided (E.164), the server attaches it as the cart's
 * buyerIdentity before applying the code, so Shopify can enforce per-customer
 * usage limits and customer-segment eligibility against that identity.
 *
 * @returns {Promise<{ cart, applied, message } | null>}
 */
export async function applyDiscountCode({ shop, cartId, code, phone }) {
  if (!shop || !cartId) return null;
  const res = await fetch(`${API_BASE}/cart/discount`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ shop, cartId, code: code || '', phone: phone || '' }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { cart: null, applied: false, message: data?.error || `Помилка: ${res.status}` };
  }
  return data;
}

export async function submitOrder(payload) {
  return callApi('/order', { method: 'POST', body: JSON.stringify(payload) });
}
